/**
 * The version a controlled document carries in its own text — the number an
 * auditor expects to see ("V3.1.0"), as opposed to the application's revision
 * counter (1, 2, 3 …). Looked for, in order of trust:
 *
 *   1. a title suffix — "Acceptable Use Policy V3.1.0";
 *   2. a document-control row — `| **Version** | 2.0.1 |` or `**Version:** V1.4`;
 *   3. the highest entry of the first table with a "Version" column (the
 *      version-history table every policy opens with);
 *   4. an inline "Version: 2.5" mention (spreadsheet title cells).
 *
 * The result is the bare label ("3.1.0") or '' when the document names no
 * version. Only dotted numbers are accepted, so a date or a reference such as
 * "QMS/IRS/037" never becomes a version.
 */

const VERSION_LABEL = /^\d+(?:\.\d+){0,3}$/;
const TITLE_SUFFIX = /\bV\s?(\d+(?:\.\d+){0,3})\s*$/i;
const CONTROL_ROW = /^\s*\|?\s*(?:\*\*)?\s*(?:document\s+)?version\s*(?:\*\*)?\s*(?::\s*(?:\*\*)?|\|)\s*(?:\*\*)?\s*v?\s?(\d+(?:\.\d+){0,3})(?![\d.])/im;
const INLINE_MENTION = /\bversion\s*:\s*v?\s?(\d+(?:\.\d+){0,3})(?![\d.])/i;
const SEPARATOR_CELL = /^:?-{2,}:?$/;

/** "V3.1.0" / "v2" / " 3.1.0 " → "3.1.0"; anything that is not a dotted number → ''. */
export function normaliseVersionLabel(raw: string): string {
  const value = raw.trim().replace(/^v\s?(?=\d)/i, '').trim();
  return VERSION_LABEL.test(value) ? value : '';
}

/** Numeric, part by part: 1.10 > 1.9, 2 == 2.0. */
export function compareVersionLabels(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function cleanCell(cell: string): string {
  return cell.replace(/\*\*|__|`/g, '').trim();
}

/** Cells of a GFM table row, without the empties the outer pipes produce. */
function splitRow(line: string): string[] {
  const cells = line.split('|');
  if (cells.length > 0 && cells[0]!.trim() === '') cells.shift();
  if (cells.length > 0 && cells[cells.length - 1]!.trim() === '') cells.pop();
  return cells;
}

/**
 * The highest version in the first table that has a "Version" column. Only
 * the first such table counts: a register's own version history comes first,
 * and the sheets after it list other documents' versions.
 */
function versionHistoryMax(markdown: string): string {
  let column: number | null = null;
  let inTable = false;
  let best = '';
  for (const line of markdown.split('\n')) {
    const isRow = /^\s*\|/.test(line);
    if (!isRow) {
      if (inTable && best !== '') return best;
      inTable = false;
      column = null;
      continue;
    }
    inTable = true;
    const cells = splitRow(line);
    if (column === null) {
      const index = cells.findIndex((cell) => /^version$/i.test(cleanCell(cell)));
      if (index !== -1) column = index;
      continue;
    }
    if (cells.every((cell) => SEPARATOR_CELL.test(cell.trim()))) continue;
    const value = normaliseVersionLabel(cleanCell(cells[column] ?? ''));
    if (value !== '' && (best === '' || compareVersionLabels(value, best) > 0)) best = value;
  }
  return best;
}

export function parseDocumentVersion(title: string, markdown: string): string {
  const fromTitle = TITLE_SUFFIX.exec(title);
  if (fromTitle) return fromTitle[1]!;
  const fromControlRow = CONTROL_ROW.exec(markdown);
  if (fromControlRow) return fromControlRow[1]!;
  const fromHistory = versionHistoryMax(markdown);
  if (fromHistory !== '') return fromHistory;
  const fromInline = INLINE_MENTION.exec(markdown);
  return fromInline ? fromInline[1]! : '';
}
